import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { categories, allVideos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  const category = categories.find((c) => c.slug === slug);
  const categoryVideos = allVideos.filter((v) => v.category === slug);

  const sortedVideos = [...categoryVideos].sort((a, b) => {
    if (sortBy === 'popular') return parseFloat(b.views) - parseFloat(a.views);
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const totalPages = Math.ceil(sortedVideos.length / perPage);
  const paginatedVideos = sortedVideos.slice((currentPage - 1) * perPage, currentPage * perPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [slug, sortBy]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6 lg:px-12">
          <div className="max-w-6xl mx-auto text-center py-20">
            <h2 className="text-2xl font-bold text-foreground-950 mb-4">分类未找到</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <section className="py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-2">
                    一起草{category.name}在线观看
                  </h1>
                  <p className="text-sm text-foreground-500">
                    {category.description} · 共 <strong>{categoryVideos.length}</strong> 部精彩内容
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 mr-1">排序：</span>
                  {[
                    { key: 'latest', label: '最新' },
                    { key: 'popular', label: '最热' },
                    { key: 'rating', label: '评分' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key as typeof sortBy)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-accent-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {paginatedVideos.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {paginatedVideos.map((video) => (
                      <VideoCard key={video.id} {...video} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-12">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm cursor-pointer transition-all duration-300 whitespace-nowrap ${
                          currentPage === 1
                            ? 'text-foreground-300 cursor-not-allowed'
                            : 'text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-9 h-9 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                            page === currentPage
                              ? 'bg-primary-500 text-white'
                              : 'text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm cursor-pointer transition-all duration-300 whitespace-nowrap ${
                          currentPage === totalPages
                            ? 'text-foreground-300 cursor-not-allowed'
                            : 'text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-500">暂无内容，敬请期待</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}